/**
 * The EdConnect BIND round trip, shared by its two return legs.
 *
 * A bind sends the browser to EdCity and gets it back carrying two query
 * markers. There are two ways back, and they must agree on what the markers
 * mean or the same failure reads differently depending on how the user's
 * browser happened to behave:
 *
 *   1. NEW WINDOW (the normal path) - the popup lands on the static relay page
 *      public/edconnect_bind_done.html, which forwards the markers to this tab
 *      and closes itself. The composer tab never navigates, so the dialog and
 *      whatever the teacher had open stay exactly as they were.
 *   2. SAME TAB (the fallback, when the popup is blocked) - the markers arrive
 *      on the composer's own URL and RootPage reads them on the next mount.
 *
 * Both legs call bindOutcome() so the vocabulary lives in one place. It returns
 * a Localization KEY rather than a translated string, so this module stays free
 * of UI imports and the caller keeps control of how it is shown.
 */

/** postMessage `type`, and the BroadcastChannel / localStorage key. */
export const BIND_MESSAGE_TYPE = 'ev:edconnect_bind';
export const BIND_CHANNEL_NAME = 'ev_edconnect_bind';
export const BIND_STORAGE_KEY = 'ev_edconnect_bind';
/** Names the popup, so a second click reuses the window instead of stacking. */
export const BIND_WINDOW_NAME = 'ev_edconnect_bind';

/**
 * Where EdCity's callback should send the popup: the relay page next to the
 * app's own index.html.
 *
 * Derived from the running page rather than hard-coded, because deploy.sh takes
 * the target folder as an argument (client_beta, client_v2, client_v2_2026...),
 * so a literal '/client_beta/' would break every other deployment. Under
 * HashRouter the route lives in the fragment, so pathname is always the app's
 * own directory - dropping the last segment handles both '/client_beta/' and
 * '/client_beta/index.html'.
 *
 * The server allows this URL already: _safe_return in the backend is a prefix
 * allow-list over 'https://ev-cuhk.net/' (plus the api host and localhost:3001),
 * so any path underneath passes without a backend change.
 */
export function bindReturnUrl() {
  const dir = window.location.pathname.replace(/[^/]*$/, '');
  return window.location.origin + dir + 'edconnect_bind_done.html';
}

/**
 * Map a failure code to the string that explains it.
 *
 * Shared by both the redirect markers and the JSON body of the finish call,
 * because the server deliberately speaks ONE vocabulary on both -
 * `already_linked_to_other`, `already_linked_created`, `school_mismatch` - and
 * two copies of this mapping is how the two legs drift apart.
 *
 * Anything unrecognised falls back to the generic failure rather than throwing:
 * a code this build has not heard of still has to say something to the user.
 * `bind_mismatch` and `bind_expired` land there on purpose - an honest client
 * cannot produce either (the same tab starts and finishes the round trip), so
 * copy of their own would only describe a state no user is in.
 */
export function bindErrorKey(code) {
  const err = code || '';
  if (err === 'already_linked_to_other') {
    return 'EDCONNECT_BIND_ALREADY_OTHER';
  }
  if (err === 'school_mismatch') {
    // The account's school and the EdCity school both resolved, and to
    // DIFFERENT schools - the server refuses the link.
    return 'EDCONNECT_BIND_SCHOOL_MISMATCH';
  }
  if (err === 'already_linked_created') {
    // The conflicting link's owner is an account EdCity login auto-created -
    // the no-migrate reminder, not the generic "already linked" copy.
    return 'EDCITY_CREATED_ACCOUNT_NO_MIGRATE';
  }
  if (err === 'account_already_linked') {
    // THIS account already holds an EdCity link (server rule, 2026-09-17: one
    // EdCity account per EduVenture-AI account).
    //
    // The OPPOSITE DIRECTION from already_linked_to_other, and the distinction
    // is the whole reason it gets its own key. That one means the EdCity
    // identity belongs to somebody else's account - a dead end the teacher
    // cannot fix alone. This one means their own account is already spoken for,
    // which they fix in this very dialog by removing the existing link. Falling
    // back to the generic copy would hide the one action that works.
    return 'EDCONNECT_BIND_ACCOUNT_HAS_ONE';
  }
  return 'EDCONNECT_BIND_FAILED';
}

/**
 * What the outcome markers mean.
 *
 * `bind` is the `edconnect_bind` marker and `error` is the failure code. Note
 * the key is `error`, NOT `edconnect_error` - the bind leg has its own
 * vocabulary, and the login one must not be read here.
 *
 * THREE shapes, and the middle one is the current success path:
 *
 *   '2' -> {pending: true, bindCode} - EdCity verified the identity and the
 *          server parked it on a single-use ticket. NOTHING IS LINKED YET. The
 *          caller must POST the code back with its bearer
 *          (AuthService.finishEdCityBind) - that request is what performs the
 *          link, and its bearer is what names the account to link it to. The
 *          server stopped doing this on the redirect because a redirect cannot
 *          prove which browser is finishing the round trip, so the account
 *          could be chosen by whoever started it rather than by whoever
 *          returned.
 *   '1' -> the OLD success shape, from a server that still linked in the
 *          callback. Kept so a composer deployed ahead of the backend keeps
 *          working; delete once every environment is on the ticket flow.
 *   '0' -> a failure that happened before any ticket existed.
 */
export function bindOutcome(payload) {
  const bind = payload && payload.bind != null ? String(payload.bind) : '';
  if (bind === '2') {
    const code = (payload && payload.bindCode) || '';
    if (code) {
      return { pending: true, bindCode: code };
    }
    // '2' with no code cannot come from our own callback; treat a hand-edited
    // URL as the generic failure rather than starting a round trip with no
    // credential in it.
    return { ok: false, stringKey: 'EDCONNECT_BIND_FAILED' };
  }
  if (bind === '1') {
    // `status` is success|already_linked. Both are a good outcome from the
    // user's side, so they deliberately read the same.
    return { ok: true, stringKey: 'EDCONNECT_BIND_SUCCESS' };
  }
  return { ok: false, stringKey: bindErrorKey(payload && payload.error) };
}

/**
 * Is this bind attempt missing the password the server will demand?
 *
 * `edcity_bind_start` has re-proven the account's own password since the
 * 2026-08-24 ticket flow ("linking is an account takeover primitive if a
 * walk-up session suffices"), exactly as the google and apple link routes do.
 * The composer sent that password from a field it only rendered next to the
 * Google/Apple buttons - and both providers are currently switched off - so
 * every EdConnect bind went out with an empty password and came back 403,
 * which the dialog then reported as "check your password and try again" to a
 * teacher who had never been shown a password box.
 *
 * Returns a Localization KEY when the attempt cannot succeed, or null to let it
 * go. `hasPassword` is `user.has_password` from the API, and the test is
 * deliberately `=== true`: an account we KNOW has a password is refused here
 * rather than after a popup and a round trip, while an older API that does not
 * report the flag defers to the server, whose answer for a password-less
 * (SSO-provisioned) account is to allow the bind with no password at all.
 */
export function bindPasswordGateKey({ hasPassword, password } = {}) {
  if (hasPassword === true && !password) {
    return 'LINK_PASSWORD_NEEDED';
  }
  return null;
}

/**
 * Is this account already holding an EdCity link?
 *
 * The client half of the one-EdCity-account-per-account rule, and a sibling of
 * bindPasswordGateKey in every way: a pure pre-flight predicate that returns a
 * Localization KEY when the attempt cannot succeed, so the dialog can refuse
 * before the popup instead of after a full CAS round trip.
 *
 * NOT the enforcement. The server refuses the same thing twice over (at
 * bind_start and again at the write), because the OTHER bind door -
 * EdCityUnlinkedDialog's "log in and link" - is reached by a teacher who is not
 * signed in yet, and no client-side check can see an account the browser has
 * not authenticated to. This exists to make the ordinary path say something
 * useful one click sooner, nothing more.
 *
 * `linked` is true when an active edconnect link is known. Deliberately a
 * BOOLEAN the caller computes rather than a list this module inspects: the two
 * available sources disagree in one important case (see the dialog), and
 * choosing between them is the caller's business.
 */
export function bindAlreadyLinkedKey({ linked } = {}) {
  return linked ? 'EDCONNECT_BIND_ACCOUNT_HAS_ONE' : null;
}

/**
 * The finish call's {ok, status, body} turned into the same {ok, stringKey}
 * shape settleBind and RootPage already render.
 */
export function bindFinishOutcome(result) {
  if (result && result.ok) {
    return { ok: true, stringKey: 'EDCONNECT_BIND_SUCCESS' };
  }
  const body = (result && result.body && typeof result.body === 'object')
    ? result.body : {};
  return { ok: false, stringKey: bindErrorKey(body.error) };
}

export default {
  BIND_MESSAGE_TYPE,
  BIND_CHANNEL_NAME,
  BIND_STORAGE_KEY,
  BIND_WINDOW_NAME,
  bindReturnUrl,
  bindOutcome,
  bindErrorKey,
  bindPasswordGateKey,
  bindAlreadyLinkedKey,
  bindFinishOutcome,
};
